import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';

const LANG_KEY = 'freyr-lang';

@Injectable({ providedIn: 'root' })
export class AdminI18nService {
  private readonly http = inject(HttpClient);
  private dictionary: Record<string, unknown> = {};

  currentLang = 'fr';

  readonly languages = [
    { code: 'fr', flagSrc: 'assets/flags/fr.svg', labelKey: 'admin.nav.languages.fr' },
    { code: 'en', flagSrc: 'assets/flags/gb.svg', labelKey: 'admin.nav.languages.en' }
  ];

  async init(): Promise<void> {
    const saved = localStorage.getItem(LANG_KEY);
    const browser = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'fr';
    const lang = saved && ['fr', 'en'].includes(saved) ? saved : (browser.startsWith('en') ? 'en' : 'fr');
    await this.use(lang, false);
  }

  async use(lang: string, persist = true): Promise<void> {
    if (!['fr', 'en'].includes(lang)) {
      lang = 'fr';
    }

    this.dictionary = await firstValueFrom(
      this.http.get<Record<string, unknown>>(`assets/admin-i18n/${lang}.json`)
    );
    this.currentLang = lang;

    if (persist) {
      localStorage.setItem(LANG_KEY, lang);
    }
  }

  t(key: string, params?: Record<string, string | number | null | undefined>): string {
    const value = this.resolveKey(this.dictionary, key);
    const text = typeof value === 'string' ? value : key;
    return this.interpolate(text, params);
  }

  private resolveKey(source: Record<string, unknown>, key: string): unknown {
    return key.split('.').reduce<unknown>((current, segment) => {
      if (!current || typeof current !== 'object') {
        return undefined;
      }
      return (current as Record<string, unknown>)[segment];
    }, source);
  }

  private interpolate(text: string, params?: Record<string, string | number | null | undefined>): string {
    if (!params) {
      return text;
    }
    return text.replace(/\{\{\s*(\w+)\s*}}/g, (_, token: string) => {
      const value = params[token];
      return value === null || value === undefined ? '' : String(value);
    });
  }
}
